// src/pages/analytics/filterPane.tsx
//
// Analytics filter pane — the PBI-style slicer set (feature analytics.filterpane,
// default OFF; enabled per tenant). One state shared by every sales tab that
// renders <FilterPane /> (YoY, 7 vs 28, GP, Trends): set "Price level 7" on
// one tab and it is still set on the next, the way a PBI report's slicers
// carry across pages. Persisted per browser in localStorage (best-effort).
//
// Params map 1:1 onto the agent's PaneFilterSql (agent 3.20.0+): salesType
// (net | gross | returns) plus f* slicers. Older agents ignore unknown params,
// so the pane degrades to "no effect" rather than an error.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SlidersHorizontal, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import { SavedViews } from '../../components/SavedViews';
import { FilterSelect } from './salesFilterBar';
import { subcategoryOptions } from './itemSubcategories';
import { useFilterPane, activeCount, type PaneState, type SalesType } from './filterPaneState';

const SALES_TYPES: { value: SalesType; label: string }[] = [
  { value: 'net', label: 'Net' },
  { value: 'gross', label: 'Gross' },
  { value: 'returns', label: 'Returns' },
];

const DOWS = [
  { n: 1, label: 'Mon' }, { n: 2, label: 'Tue' }, { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' }, { n: 5, label: 'Fri' }, { n: 6, label: 'Sat' }, { n: 7, label: 'Sun' },
];

const YES_NO = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];

const pill = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;

const opts = (xs: string[] | undefined) => (xs ?? []).map(x => ({ value: x, label: x }));

/**
 * The slicer panel. Always-visible strip: Sales (Net/Gross/Returns), active
 * count, expand toggle, reset. Expanded: customer, rep and item slicers plus
 * day-of-week chips. Lists whose data is missing on this tenant are hidden.
 */
/**
 * Saved views for an analytics tab. With the pane on, a view also carries the
 * pane slicers (key `__pane`) and applying it restores them; views saved
 * without one (older views, pane off) leave the pane as it is.
 */
export function AnalyticsSavedViews({ page, getState, applyState }: {
  page: string;
  getState: () => Record<string, unknown>;
  applyState: (s: Record<string, unknown>) => void;
}) {
  const pane = useFilterPane();
  return (
    <SavedViews
      page={page}
      getState={() => (pane.enabled ? { ...getState(), __pane: pane.state } : getState())}
      applyState={(s) => {
        applyState(s);
        if (pane.enabled && s.__pane && typeof s.__pane === 'object') pane.replace(s.__pane as Partial<PaneState>);
      }}
    />
  );
}

export function FilterPane({ note }: { note?: string }) {
  const pane = useFilterPane();
  const [open, setOpen] = useState(false);
  const { data: f } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
    enabled: pane.enabled,
  });
  if (!pane.enabled) return null;

  const s = pane.state;
  const n = activeCount(s);
  const subcats = subcategoryOptions(f, s.itemCategory);
  const toggleDow = (d: number) =>
    pane.set({ dow: s.dow.includes(d) ? s.dow.filter(x => x !== d) : [...s.dow, d] });

  return (
    <div className="rounded-xl bg-slate-800/20 border border-slate-700/30">
      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
        <button onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-300 hover:text-white">
          <SlidersHorizontal size={13} /> Filters
          {n > 0 && <span className="px-1.5 rounded-full bg-blue-500/25 text-blue-200 text-[10px] tabular-nums">{n}</span>}
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-slate-500 uppercase tracking-wider mr-1">Sales</span>
          {SALES_TYPES.map(t => (
            <button key={t.value} onClick={() => pane.set({ salesType: t.value })} className={pill(s.salesType === t.value)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-slate-500 uppercase tracking-wider mr-1">Date</span>
          <button onClick={() => pane.set({ dateBasis: 'invoice' })} className={pill(s.dateBasis !== 'ship')}
            title="Bucket and filter by the invoice date">Invoice</button>
          <button onClick={() => pane.set({ dateBasis: 'ship' })} className={pill(s.dateBasis === 'ship')}
            title="Bucket and filter by the ship date (falls back to the invoice date when blank)">Ship</button>
        </div>
        {note && <span className="text-[11px] text-slate-500 italic">{note}</span>}
        <div className="flex-1" />
        {n > 0 && (
          <button onClick={pane.reset} title="Clear every filter"
            className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
            <RotateCcw size={11} /> Clear filters
          </button>
        )}
      </div>

      {open && (
        <div className="flex flex-wrap items-end gap-3 px-4 pb-4 pt-1 border-t border-slate-700/30">
          {(f?.priceLevels?.length ?? 0) > 0 && (
            <FilterSelect label="Price Level" value={s.priceLevel} onChange={v => pane.set({ priceLevel: v })} options={opts(f?.priceLevels)} />
          )}
          {(f?.areas?.length ?? 0) > 0 && (
            <FilterSelect label="Area" value={s.area} onChange={v => pane.set({ area: v })} options={opts(f?.areas)} />
          )}
          {(f?.states?.length ?? 0) > 0 && (
            <FilterSelect label="State" value={s.state} onChange={v => pane.set({ state: v })} options={opts(f?.states)} />
          )}
          {(f?.cities?.length ?? 0) > 0 && (
            <FilterSelect label="City" value={s.city} onChange={v => pane.set({ city: v })} options={opts(f?.cities)} />
          )}
          <FilterSelect label="Customer Category" value={s.custCategory} onChange={v => pane.set({ custCategory: v })} options={opts(f?.categories)} />
          <FilterSelect label="Credit Hold" value={s.creditHold} onChange={v => pane.set({ creditHold: v as PaneState['creditHold'] })} options={YES_NO} />
          <FilterSelect label="Salesman" value={s.salesman} onChange={v => pane.set({ salesman: v })} options={opts(f?.salesmen)} />
          {(f?.salesmen1?.length ?? 0) > 0 && (
            <FilterSelect label="Salesman 2" value={s.salesman2} onChange={v => pane.set({ salesman2: v })} options={opts(f?.salesmen1)} />
          )}
          <FilterSelect label="Route" value={s.driver} onChange={v => pane.set({ driver: v })} options={opts(f?.drivers)} />
          <FilterSelect label="Item Category" value={s.itemCategory} onChange={v => pane.set({ itemCategory: v, itemSubcategory: '' })} options={opts(f?.itemCategories)} />
          {subcats && (
            <FilterSelect label="Item Subcategory" value={s.itemSubcategory} onChange={v => pane.set({ itemSubcategory: v })} options={opts(subcats)} />
          )}
          {(f?.vendors?.length ?? 0) > 0 && (
            <FilterSelect label="Vendor" value={s.vendor} onChange={v => pane.set({ vendor: v })}
              options={(f?.vendors ?? []).map(x => ({ value: x.code, label: x.name === x.code ? x.code : `${x.name} (${x.code})` }))} />
          )}
          {(f?.itemTypes?.length ?? 0) > 0 && (
            <FilterSelect label="Item Type" value={s.itemType} onChange={v => pane.set({ itemType: v })} options={opts(f?.itemTypes)} />
          )}
          <FilterSelect label="Discontinued" value={s.discontinued} onChange={v => pane.set({ discontinued: v as PaneState['discontinued'] })} options={YES_NO} />
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Day of Week</span>
            <div className="flex items-center gap-1">
              {DOWS.map(d => (
                <button key={d.n} onClick={() => toggleDow(d.n)} className={pill(s.dow.includes(d.n))}>{d.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
